import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";
import { trackEvent } from "@/lib/analytics";
import { Loader2, Lock, Sparkles, MapPin, Star as StarIcon, Coins, MessageCircle } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import MatchRevealModal from "@/components/MatchRevealModal";
import NumericRating from "@/components/NumericRating";
import { useAuth } from "@/lib/auth";

export default function PotentialMatches() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState({ potential_matches: [], count: 0, threshold: 8, min_ratings: 3, subscribed: false });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [revealMatch, setRevealMatch] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/potential-matches");
      setData(data);
    } catch (err) {
      toast.error(errorMessage(err, "Could not load golden matches"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Auto-open reveal modal if returning from a successful Stripe reveal (?reveal=<user_id>)
  useEffect(() => {
    const revealUid = searchParams.get("reveal");
    if (!revealUid || loading) return;
    const item = data.potential_matches.find((x) => x.user.id === revealUid && x.unlocked);
    if (item) {
      setRevealMatch({ match_id: item.match_id, user: item.user });
      const next = new URLSearchParams(searchParams);
      next.delete("reveal");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, data.potential_matches, loading, setSearchParams]);

  const credits = user?.reveal_credits || 0;
  const subscribed = user?.subscription_active;

  const revealWithCredit = async (targetId) => {
    setBusyId(targetId);
    try {
      const { data: res } = await api.post(`/profiles/${targetId}/spend-credit`);
      trackEvent("reveal_credit_spent", { target_id: targetId, method: "credit", source: "golden_matches" });
      await refresh();
      const { data: fresh } = await api.get("/potential-matches");
      setData(fresh);
      const unlocked = fresh.potential_matches.find((x) => x.user.id === targetId && x.unlocked);
      if (unlocked) {
        setRevealMatch({ match_id: res.match_id || unlocked.match_id, user: unlocked.user });
      }
    } catch (err) {
      toast.error(errorMessage(err, "Could not reveal"));
    } finally {
      setBusyId(null);
    }
  };

  const revealPaid = async (targetId) => {
    setBusyId(targetId);
    try {
      const { data: res } = await api.post(`/profiles/${targetId}/reveal-checkout`, {
        origin_url: window.location.origin,
      });
      trackEvent("begin_checkout", {
        currency: "USD", value: 10,
        items: [{ item_id: targetId, item_name: "reveal", price: 10 }],
        source: "golden_matches",
      });
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(errorMessage(err, "Could not start checkout"));
      setBusyId(null);
    }
  };

  const ratePhotoInline = async (userId, photoId, rating) => {
    try {
      const { data: r } = await api.post("/rate", { photo_id: photoId, rating });
      trackEvent("rate_photo", { score: rating, is_high: rating >= 8, source: "golden_matches" });
      setData((prev) => ({
        ...prev,
        potential_matches: prev.potential_matches.map((it) => {
          if (it.user.id !== userId) return it;
          const fresh = (r.owner?.photos || []).find((p) => p.id === photoId);
          if (!fresh) return it;
          const nextPhotos = (it.user.photos || []).map((p) => p.id === photoId ? { ...p, ...fresh } : p);
          return { ...it, user: { ...it.user, photos: nextPhotos, overall_rating: r.owner?.overall_rating ?? it.user.overall_rating } };
        }),
      }));
      if (r.streak?.credit_awarded) toast.success(`\uD83D\uDD25 ${r.streak.streak}-day streak! +1 credit`, { duration: 4200 });
      else toast.success(`Rated ${rating}/10`);
      if (refresh) refresh();
    } catch (err) {
      toast.error(errorMessage(err, "Could not save rating"));
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#722f37]" />
      </div>
    );
  }

  const items = data.potential_matches || [];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#c39d2b]" />
            <span className="text-sm uppercase tracking-[0.28em] font-bold text-[#722f37]">Golden matches</span>
          </div>
          <h1 className="font-serif-display text-4xl md:text-5xl mt-2">
            {items.length === 0
              ? "No golden matches yet."
              : items.length === 1
              ? "1 profile is golden."
              : `${items.length} profiles are golden.`}
          </h1>
          <p className="mt-3 text-lg text-[#5c5c5c]">
            Members whose photos rate <span className="font-semibold text-[#722f37]">{data.threshold}+ out of 10</span> across at least {data.min_ratings} community ratings. Reveal to see who they are and start a conversation.
          </p>
        </div>
        {subscribed ? (
          <div className="glass px-5 py-3 rounded-full flex items-center gap-2 text-sm text-[#722f37] font-medium" data-testid="potential-subscribed">
            <Sparkles className="w-4 h-4 text-[#c39d2b]" /> Membership active — every reveal free
          </div>
        ) : (
          <div className="flex items-center gap-3 flex-wrap">
            <div className="glass px-5 py-3 rounded-full flex items-center gap-2 text-sm text-[#722f37] font-medium" data-testid="potential-credits-balance">
              <Coins className="w-4 h-4 text-[#c39d2b]" /> {credits} credit{credits === 1 ? "" : "s"}
            </div>
            <Link to="/pricing" className="btn-outline !py-2 !px-4 text-sm" data-testid="potential-get-more">Get credits</Link>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="p-16 rounded-2xl bg-white border border-[#e5e0d8] card-elev text-center" data-testid="potential-empty">
          <Sparkles className="w-8 h-8 text-[#c39d2b] mx-auto" />
          <h3 className="font-serif-display text-3xl mt-4 text-[#722f37]">No golden profiles yet.</h3>
          <p className="mt-3 text-[#5c5c5c] text-lg">
            Rate photos on Discover — profiles need {data.min_ratings}+ ratings averaging {data.threshold}+/10 to appear here.
          </p>
          <div className="mt-6"><Link to="/discover" className="btn-primary" data-testid="potential-go-discover">Rate photos</Link></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="potential-grid">
          {items.map((it) => (
            <GoldenCard
              key={it.user.id}
              item={it}
              credits={credits}
              subscribed={subscribed}
              onRevealCredit={() => revealWithCredit(it.user.id)}
              onRevealPaid={() => revealPaid(it.user.id)}
              onRatePhoto={(photoId, score) => ratePhotoInline(it.user.id, photoId, score)}
              busy={busyId === it.user.id}
            />
          ))}
        </div>
      )}

      {revealMatch && (
        <MatchRevealModal match={revealMatch} onClose={() => setRevealMatch(null)} />
      )}
    </div>
  );
}

function GoldenCard({ item, credits, subscribed, onRevealCredit, onRevealPaid, onRatePhoto, busy }) {
  const u = item.user;
  const photo = u.photos?.[0];
  const rating = u.overall_rating || 0;
  const unlocked = item.unlocked;
  const [ratingBusy, setRatingBusy] = React.useState(false);

  const handleRate = async (score) => {
    setRatingBusy(true);
    try { await onRatePhoto(photo.id, score); }
    finally { setRatingBusy(false); }
  };

  return (
    <div className="rounded-2xl overflow-hidden bg-white border border-[#e5e0d8] card-elev flex flex-col" data-testid={`golden-card-${u.id}`}>
      <div className="relative aspect-[4/5] bg-[#eae5d9] overflow-hidden">
        {photo?.data_url ? (
          <Link
            to={`/u/${u.id}`}
            className="block w-full h-full"
            data-testid={`golden-view-profile-${u.id}`}
            aria-label={unlocked ? `View ${u.display_name}'s profile` : "Preview locked profile"}
          >
            <img
              src={photo.data_url}
              alt={unlocked ? (u.display_name || "Golden match") : "Blurred profile"}
              className={`w-full h-full object-cover ${unlocked ? "" : "locked-blur"}`}
              data-testid={`golden-photo-${u.id}`}
            />
          </Link>
        ) : (
          <div className={`w-full h-full ${unlocked ? "" : "locked-blur"}`} style={{ background: "linear-gradient(135deg,#c9b8a0,#a08a70)" }} />
        )}

        {/* Rating badge — always visible */}
        <div className="absolute top-3 left-3 glass rounded-full px-3 py-1.5 flex items-center gap-1 text-xs font-semibold text-[#722f37]" data-testid={`golden-rating-${u.id}`}>
          <StarIcon className="w-3.5 h-3.5 text-[#c39d2b] fill-[#c39d2b]" />
          {rating.toFixed(1)}/10 · {u.rating_count} rating{u.rating_count === 1 ? "" : "s"}
        </div>

        {u.verified && (
          <div className="absolute top-3 right-3">
            <VerifiedBadge size={12} />
          </div>
        )}

        {unlocked ? (
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-serif-display text-white text-3xl leading-tight" data-testid={`golden-name-${u.id}`}>
                {u.display_name}, <span className="opacity-80">{u.age}</span>
              </h3>
            </div>
            {u.location && (
              <div className="mt-1 flex items-center gap-1 text-white/85 text-xs">
                <MapPin className="w-3 h-3" /> {u.location}
              </div>
            )}
          </div>
        ) : (
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
            <div className="font-serif-display text-white text-2xl leading-tight">
              Age {u.age}
            </div>
            {u.location && (
              <div className="mt-1 flex items-center gap-1 text-white/85 text-xs">
                <MapPin className="w-3 h-3" /> {u.location}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="p-4 flex flex-col gap-2">
        {/* Inline rate strip — visible only when the photo isn't blurred (rating a blur is meaningless). */}
        {unlocked && photo?.id && (
          <div className="rounded-xl bg-[#faf9f6] border border-[#e5e0d8] px-3 py-2.5 mb-1" data-testid={`golden-rate-strip-${u.id}`}>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#722f37]">Rate this photo</span>
              {photo.my_rating != null && (
                <span className="text-[10px] text-[#c39d2b] font-semibold" data-testid={`golden-my-rating-${u.id}`}>
                  You: {photo.my_rating}/10
                </span>
              )}
            </div>
            <NumericRating
              value={photo.my_rating || 0}
              onChange={handleRate}
              readOnly={ratingBusy}
              testId={`golden-rate-${u.id}`}
              compact
            />
          </div>
        )}

        {/* Locked variant — a "Reveal to rate" teaser that fires the same reveal path. */}
        {!unlocked && photo?.id && (
          <button
            type="button"
            onClick={subscribed || credits > 0 ? onRevealCredit : onRevealPaid}
            disabled={busy}
            className="group rounded-xl bg-[#faf9f6] border border-dashed border-[#c39d2b]/60 hover:border-[#c39d2b] hover:bg-[#c39d2b]/8 px-3 py-2.5 mb-1 text-left transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid={`golden-rate-locked-${u.id}`}
            aria-label="Reveal this profile to rate the photo"
          >
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] font-bold text-[#c39d2b]">
                <Lock className="w-3 h-3" /> Reveal to rate
              </span>
              <span className="text-[10px] text-[#5c5c5c] font-semibold group-hover:text-[#722f37]">
                {subscribed ? "free with Prime" : credits > 0 ? `1 credit \u2192` : "$10 \u2192"}
              </span>
            </div>
            <div className="flex items-center gap-1 opacity-60 select-none pointer-events-none" aria-hidden>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <span key={n} className="w-8 h-8 rounded-full bg-white text-[#722f37] border border-[#d6cfc0] text-xs font-bold flex items-center justify-center">
                  {n}
                </span>
              ))}
            </div>
          </button>
        )}

        {unlocked ? (
          <>
            {u.bio && <p className="text-sm text-[#5c5c5c] line-clamp-3">{u.bio}</p>}
            <Link
              to={`/chat/${item.match_id}`}
              className="mt-2 w-full flex items-center justify-center gap-2 rounded-full bg-[#722f37] text-white py-2.5 text-sm font-medium hover:bg-[#8c3a45] transition-colors"
              data-testid={`golden-chat-${u.id}`}
            >
              <MessageCircle className="w-4 h-4" /> Start chatting
            </Link>
          </>
        ) : subscribed ? (
          <button
            onClick={onRevealCredit}
            disabled={busy}
            className="btn-primary w-full flex items-center justify-center gap-2 !py-2.5 text-sm"
            data-testid={`golden-reveal-sub-${u.id}`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Reveal (free with membership)
          </button>
        ) : credits > 0 ? (
          <>
            <button
              onClick={onRevealCredit}
              disabled={busy}
              className="btn-primary w-full flex items-center justify-center gap-2 !py-2.5 text-sm"
              data-testid={`golden-reveal-credit-${u.id}`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
              Use 1 credit ({credits} left)
            </button>
            <button
              onClick={onRevealPaid}
              disabled={busy}
              className="text-xs text-[#5c5c5c] underline hover:text-[#722f37] mt-1"
              data-testid={`golden-reveal-paid-${u.id}`}
            >
              or pay $10 once
            </button>
          </>
        ) : (
          <>
            <button
              onClick={onRevealPaid}
              disabled={busy}
              className="btn-gold w-full flex items-center justify-center gap-2 !py-2.5 text-sm"
              data-testid={`golden-reveal-paid-${u.id}`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Reveal for $10
            </button>
            <Link to="/pricing" className="text-xs text-[#5c5c5c] underline hover:text-[#722f37] mt-1 text-center">
              or save with a 3-pack / 10-pack
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
